import React, { createContext, useContext, useEffect, useState } from "react";
import { saveToken, getToken, clearToken, saveUser, getUser, clearUser } from "../utils/storage";
import authService from "../services/authService";

type LoginResult = { success: true; data?: any } | { success: false; message?: string };

type AuthContextType = {
  user: any | null;
  loading: boolean;
  login: (username: string, password: string) => Promise<LoginResult>;
  logout: () => void;
  isAuthenticated: boolean;
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<any | null>(() => getUser());
  const [loading, setLoading] = useState(false);

  const isAuthenticated = Boolean(getToken());

  // Use authService for login
  const login = async (username: string, password: string): Promise<LoginResult> => {
    setLoading(true);
    try {
      const data = await authService.login({ username, password });
      // console.log(" Login API response (AuthContext):", data);

      if (!data?.token) {
        setLoading(false);
        return { success: false, message: data?.message || "Login failed" };
      }

      saveToken(data.token);
      if (data?.user) {
        saveUser(data.user);
        setUser(data.user);
      }

      setLoading(false);
      return { success: true, data };
    } catch (err: any) {
      console.error("❌ Login error (AuthContext):", err);
      setLoading(false);
      const apiMessage =
        err?.response?.data?.message ||
        err?.message ||
        "Network error";

      return { success: false, message: apiMessage };
    }
  };

  // Use authService for logout
 const logout = async () => {
  try {
    await authService.logout(); 
  } catch {}
  clearToken();
  clearUser();
  setUser(null);
};


  useEffect(() => {
    const storedUser = getUser();
    if (storedUser) setUser(storedUser);
  }, []);

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, isAuthenticated }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
};